import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Truck, Eye, EyeOff, Loader2 } from 'lucide-react';

export const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const isTest = location.pathname.startsWith('/test');
  const prefix = isTest ? '/test' : '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);
    
    if (result.success) {
      navigate(`${prefix}/dashboard`);
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="login-page bg-slate-50">
      {/* Test Environment Banner */}
      {isTest && (
        <div className="fixed top-0 left-0 right-0 bg-amber-500 text-white text-center py-2 font-bold text-sm tracking-wide z-50" data-testid="test-login-banner">
          TEST ENVIRONMENT — Data here will not affect the live system
        </div>
      )}

      {/* Cover Image - Desktop */}
      <div className="login-cover" style={isTest ? { marginTop: '36px' } : {}}>
        <div className="text-center">
          <div className={`w-20 h-20 rounded-sm flex items-center justify-center mx-auto mb-6 ${isTest ? 'bg-amber-500' : 'bg-orange-500'}`}>
            <Truck className="w-12 h-12 text-white" />
          </div>
          <h1 
            className="text-4xl font-bold text-white mb-4 tracking-tight"
            style={{ fontFamily: 'Barlow Condensed' }}
          >
            RTMS COMPLIANCE PRO
          </h1>
          <p className="text-slate-400 text-lg max-w-md">
            Road Transport Management System compliance tracking for fleet operations.
          </p>
          <div className="mt-8 grid grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-3xl font-bold text-orange-500 font-mono">100%</div>
              <div className="text-sm text-slate-400 mt-1">Audit Ready</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-orange-500 font-mono">24/7</div>
              <div className="text-sm text-slate-400 mt-1">Access</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-orange-500 font-mono">ISO</div>
              <div className="text-sm text-slate-400 mt-1">Compliant</div>
            </div>
          </div>
        </div>
      </div>

      {/* Login Form */}
      <div className="login-form-container">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <div className="w-16 h-16 bg-slate-900 rounded-sm flex items-center justify-center mx-auto mb-4">
              <Truck className="w-10 h-10 text-white" />
            </div>
            <h1 
              className="text-2xl font-bold text-slate-900"
              style={{ fontFamily: 'Barlow Condensed' }}
            >
              RTMS COMPLIANCE PRO
            </h1>
          </div>

          <div className="bg-white p-8 rounded-sm border border-slate-200 shadow-sm">
            <h2 
              className="text-2xl font-bold text-slate-900 mb-2"
              style={{ fontFamily: 'Barlow Condensed' }}
            >
              SIGN IN
            </h2>
            <p className="text-slate-500 mb-6">
              Enter your credentials to access the system
            </p>

            {error && (
              <Alert variant="destructive" className="mb-6">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="name@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-12"
                  data-testid="login-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="h-12 pr-12"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white font-bold uppercase tracking-wide"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Signing In...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-slate-500">
                Don't have an account?{' '}
                <Link to="/register" className="text-orange-500 hover:text-orange-600 font-medium">
                  Contact Admin
                </Link>
              </p>
            </div>

            {/* Environment switch link */}
            <div className="mt-4 text-center">
              {isTest ? (
                <Link to="/login" className="text-sm text-amber-600 hover:text-amber-800 font-medium">
                  Switch to Live Environment
                </Link>
              ) : (
                <Link to="/test/login" className="text-sm text-slate-500 hover:text-slate-700">
                  Go to Test Environment
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
